import React from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "@/contexts/AuthContext";
import Login from "@/pages/Login";
import Layout from "@/components/Layout";
import Dashboard from "@/pages/Dashboard";
import Users from "@/pages/Users";
import Catalogs from "@/pages/Catalogs";
import Products from "@/pages/Products";
import Vademecum from "@/pages/Vademecum";
import Clients from "@/pages/Clients";
import ClientProfile from "@/pages/ClientProfile";
import CierresInventarioPage from "@/components/CierresInventario";
import Suppliers from "@/pages/Suppliers";
import Quotations from "@/pages/Quotations";
import QuotationForm from "@/pages/QuotationForm";
import SalesOrders from "@/pages/SalesOrders";
import SalesOrderForm from "@/pages/SalesOrderForm";
import Backorder from "@/pages/Backorder";
import AvisosStock from "@/pages/AvisosStock";
import Cumplimiento from "@/pages/Cumplimiento";
import AuditoriaSecuencial from "@/pages/AuditoriaSecuencial";
import DailyReport from "@/pages/DailyReport";
import Warehouse from "@/pages/Warehouse";
import Despacho from "@/pages/Despacho";
import CalendarPage from "@/pages/CalendarPage";
import TareasYCalendario from "@/pages/TareasYCalendario";
import Purchases from "@/pages/Purchases";
import ImportarSri from "@/pages/ImportarSri";
import PurchaseInvoiceForm from "@/pages/PurchaseInvoiceForm";
import Accounting from "@/pages/Accounting";
import PagosUnificado from "@/pages/PagosUnificado";
import DocumentosElectronicos from "@/pages/DocumentosElectronicos";
import Inventory from "@/pages/Inventory";
import Regulatory from "@/pages/Regulatory";
import HR from "@/pages/HR";
import Gerencia from "@/pages/Gerencia";
import Invoicing from "@/pages/Invoicing";
import InvoiceForm from "@/pages/InvoiceForm";
import SriComplementos from "@/pages/SriComplementos";
import Letters from "@/pages/Letters";
import Reports from "@/pages/Reports";
import Imports from "@/pages/Imports";
import Security from "@/pages/Security";
import Deliveries from "@/pages/Deliveries";
import Commissions from "@/pages/Commissions";
import WarehouseOps from "@/pages/WarehouseOps";
import Audit from "@/pages/Audit";
import RotacionComercial from "@/pages/RotacionComercial";
import Branches from "@/pages/Branches";
import RetencionesRecibidas from "@/pages/RetencionesRecibidas";
import InventoryReport from "@/pages/InventoryReport";
import AccountingConfig from "@/pages/AccountingConfig";
import CxcReminders from "@/pages/CxcReminders";
import NotasCreditoProveedor from "@/pages/NotasCreditoProveedor";
import SolicitudesCompra from "@/pages/SolicitudesCompra";
import CompanyConfig from "@/pages/CompanyConfig";
import Tasks from "@/pages/Tasks";
import MiCartera from "@/pages/MiCartera";
import CostComparator from "@/pages/CostComparator";
import GastosOperativos from "@/pages/GastosOperativos";
import LiquidacionesViaticos from "@/pages/LiquidacionesViaticos";
import DevolucionesProveedor from "@/pages/DevolucionesProveedor";
import AdminMigracion from "@/pages/AdminMigracion";
import AdminCorreos from "@/pages/AdminCorreos";
import AdminSriBackup from "@/pages/AdminSriBackup";
import AdminTiposRetencion from "@/pages/AdminTiposRetencion";
import AdminSecuenciales from "@/pages/AdminSecuenciales";
import SaludFinanciera from "@/pages/SaludFinanciera";
import { Toaster } from "@/components/ui/sonner";

function ProtectedRoute({ children, requireModule, requireAnyModule, requireSubmodule, requireRole }) {
  const { user, hasModule, hasSubmodule, serverDown, refetch } = useAuth();
  // ITER207: servidor temporalmente no disponible (503/502/504) — NO cerrar sesión
  if (serverDown && user === null) {
    return (
      <div className="p-10 text-center space-y-3" data-testid="server-down-screen">
        <div className="text-lg font-semibold text-[#15241d]">Servidor temporalmente no disponible</div>
        <p className="text-sm text-[#4a5c53]">
          Tu sesión sigue activa. El servidor no respondió (error 503). Reintenta en unos segundos.
        </p>
        <button onClick={refetch}
          className="px-4 py-2 rounded bg-[#205c45] text-white text-sm font-medium"
          data-testid="server-down-retry">
          Reintentar
        </button>
      </div>
    );
  }
  if (user === null) return <div className="p-10 text-center text-muted-foreground">Cargando...</div>;
  if (!user) return <Navigate to="/login" replace />;
  if (requireRole && user.role !== requireRole) return <Navigate to="/" replace />;
  if (requireModule && !hasModule(requireModule)) return <Navigate to="/" replace />;
  if (requireModule && requireSubmodule && !hasSubmodule(requireModule, requireSubmodule)) return <Navigate to="/" replace />;
  // ITER197 (Feb 2026): permitir "requireAnyModule" (OR) para páginas compartidas entre roles
  if (requireAnyModule && Array.isArray(requireAnyModule) && requireAnyModule.length > 0) {
    if (!requireAnyModule.some(m => hasModule(m))) return <Navigate to="/" replace />;
  }
  return children;
}

function AppRoutes() {
  const { user } = useAuth();
  return (
    <Routes>
      <Route path="/login" element={user ? <Navigate to="/" replace /> : <Login />} />
      <Route path="/" element={<ProtectedRoute><Layout /></ProtectedRoute>}>
        <Route index element={<Dashboard />} />
        <Route path="usuarios" element={<ProtectedRoute requireModule="seguridad"><Users /></ProtectedRoute>} />
        <Route path="admin/migracion" element={<ProtectedRoute requireRole="admin"><AdminMigracion /></ProtectedRoute>} />
        <Route path="admin/correos" element={<ProtectedRoute requireRole="admin"><AdminCorreos /></ProtectedRoute>} />
        <Route path="admin/respaldos-sri" element={<ProtectedRoute requireRole="admin"><AdminSriBackup /></ProtectedRoute>} />
        <Route path="admin/tipos-retencion" element={<ProtectedRoute requireRole="admin"><AdminTiposRetencion /></ProtectedRoute>} />
        <Route path="admin/secuenciales" element={<ProtectedRoute requireRole="admin"><AdminSecuenciales /></ProtectedRoute>} />
        <Route path="catalogos" element={<ProtectedRoute requireModule="maestros" requireSubmodule="catalogo"><Catalogs /></ProtectedRoute>} />
        <Route path="productos" element={<ProtectedRoute requireModule="maestros" requireSubmodule="productos"><Products /></ProtectedRoute>} />
        <Route path="vademecum" element={<ProtectedRoute requireModule="maestros" requireSubmodule="vademecum"><Vademecum /></ProtectedRoute>} />
        <Route path="clientes" element={<ProtectedRoute requireModule="maestros" requireSubmodule="clientes"><Clients /></ProtectedRoute>} />
        <Route path="clientes/:id/perfil" element={<ProtectedRoute requireModule="maestros" requireSubmodule="clientes"><ClientProfile /></ProtectedRoute>} />
        <Route path="proveedores" element={<ProtectedRoute requireModule="maestros" requireSubmodule="proveedores"><Suppliers /></ProtectedRoute>} />
        <Route path="compras" element={<ProtectedRoute requireModule="compras"><Purchases /></ProtectedRoute>} />
        <Route path="compras/nueva" element={<ProtectedRoute requireModule="compras"><PurchaseInvoiceForm /></ProtectedRoute>} />
        <Route path="compras/importar-sri" element={<ProtectedRoute requireModule="compras"><ImportarSri /></ProtectedRoute>} />
        <Route path="cotizaciones" element={<ProtectedRoute requireModule="ventas" requireSubmodule="cotizaciones"><Quotations /></ProtectedRoute>} />
        <Route path="cotizaciones/nueva" element={<ProtectedRoute requireModule="ventas" requireSubmodule="cotizaciones"><QuotationForm /></ProtectedRoute>} />
        <Route path="cotizaciones/:id/editar" element={<ProtectedRoute requireModule="ventas" requireSubmodule="cotizaciones"><QuotationForm /></ProtectedRoute>} />
        <Route path="pedidos" element={<ProtectedRoute requireModule="ventas" requireSubmodule="pedidos"><SalesOrders /></ProtectedRoute>} />
        <Route path="pedidos/nuevo" element={<ProtectedRoute requireModule="ventas" requireSubmodule="pedidos"><SalesOrderForm /></ProtectedRoute>} />
        <Route path="pedidos/:id/editar" element={<ProtectedRoute requireModule="ventas" requireSubmodule="pedidos"><SalesOrderForm /></ProtectedRoute>} />
        <Route path="ventas/backorder" element={<ProtectedRoute requireModule="ventas" requireSubmodule="pedidos"><Backorder /></ProtectedRoute>} />
        <Route path="avisos-stock" element={<ProtectedRoute requireModule="ventas"><AvisosStock /></ProtectedRoute>} />
        <Route path="cumplimiento" element={<ProtectedRoute requireModule="gerencia"><Cumplimiento /></ProtectedRoute>} />
        <Route path="facturacion/auditoria-secuencial" element={<ProtectedRoute requireModule="facturacion"><AuditoriaSecuencial /></ProtectedRoute>} />
        <Route path="reporte-diario" element={<ProtectedRoute requireModule="ventas"><DailyReport /></ProtectedRoute>} />
        <Route path="facturacion" element={<ProtectedRoute requireModule="facturacion" requireSubmodule="facturas"><Invoicing /></ProtectedRoute>} />
        <Route path="facturacion/nueva" element={<ProtectedRoute requireModule="facturacion" requireSubmodule="facturas"><InvoiceForm /></ProtectedRoute>} />
        <Route path="facturacion/:id/editar" element={<ProtectedRoute requireModule="facturacion" requireSubmodule="facturas"><InvoiceForm /></ProtectedRoute>} />
        <Route path="sri-complementos" element={<ProtectedRoute requireModule="facturacion"><SriComplementos /></ProtectedRoute>} />
        <Route path="cartas" element={<ProtectedRoute requireModule="ventas"><Letters /></ProtectedRoute>} />
        <Route path="reportes" element={<ProtectedRoute requireModule="gerencia"><Reports /></ProtectedRoute>} />
        <Route path="importaciones" element={<ProtectedRoute requireModule="maestros"><Imports /></ProtectedRoute>} />
        <Route path="seguridad" element={<ProtectedRoute><Security /></ProtectedRoute>} />
        <Route path="entregas" element={<ProtectedRoute requireModule="ventas"><Deliveries /></ProtectedRoute>} />
        <Route path="comisiones" element={<ProtectedRoute requireAnyModule={["rrhh", "contabilidad"]}><Commissions /></ProtectedRoute>} />
        <Route path="bodega-ops" element={<ProtectedRoute requireModule="bodega"><WarehouseOps /></ProtectedRoute>} />
        <Route path="auditoria" element={<ProtectedRoute requireModule="seguridad"><Audit /></ProtectedRoute>} />
        <Route path="rotacion-comercial" element={<ProtectedRoute requireModule="ventas"><RotacionComercial /></ProtectedRoute>} />
        <Route path="sucursales" element={<ProtectedRoute requireModule="maestros"><Branches /></ProtectedRoute>} />
        <Route path="retenciones-recibidas" element={<ProtectedRoute requireModule="contabilidad"><RetencionesRecibidas /></ProtectedRoute>} />
        <Route path="inventario-reporte" element={<ProtectedRoute requireModule="bodega"><InventoryReport /></ProtectedRoute>} />
        <Route path="config-contable" element={<ProtectedRoute requireModule="contabilidad"><AccountingConfig /></ProtectedRoute>} />
        <Route path="recordatorios-cobro" element={<ProtectedRoute requireModule="contabilidad"><CxcReminders /></ProtectedRoute>} />
        <Route path="notas-credito-proveedor" element={<ProtectedRoute requireModule="contabilidad"><NotasCreditoProveedor /></ProtectedRoute>} />
        <Route path="contabilidad" element={<ProtectedRoute requireModule="contabilidad"><Accounting /></ProtectedRoute>} />
        <Route path="contabilidad/pagos-unificado" element={<ProtectedRoute requireModule="contabilidad"><PagosUnificado /></ProtectedRoute>} />
        <Route path="cierres-inventario" element={<ProtectedRoute requireModule="contabilidad"><CierresInventarioPage /></ProtectedRoute>} />
        <Route path="documentos-electronicos" element={<ProtectedRoute requireModule="contabilidad"><DocumentosElectronicos /></ProtectedRoute>} />
        <Route path="bodega" element={<ProtectedRoute requireModule="bodega"><Warehouse /></ProtectedRoute>} />
        <Route path="despacho" element={<ProtectedRoute requireModule="bodega" requireSubmodule="despacho"><Despacho /></ProtectedRoute>} />
        <Route path="movimientos" element={<ProtectedRoute requireModule="bodega"><Inventory /></ProtectedRoute>} />
        <Route path="regulatorio" element={<ProtectedRoute requireModule="regulatorio"><Regulatory /></ProtectedRoute>} />
        <Route path="rrhh" element={<ProtectedRoute requireModule="rrhh"><HR /></ProtectedRoute>} />
        <Route path="gerencia" element={<ProtectedRoute requireModule="gerencia"><Gerencia /></ProtectedRoute>} />
        <Route path="salud-financiera" element={<ProtectedRoute requireModule="gerencia"><SaludFinanciera /></ProtectedRoute>} />
        <Route path="solicitudes-compra" element={<ProtectedRoute requireModule="gerencia"><SolicitudesCompra /></ProtectedRoute>} />
        <Route path="config-empresa" element={<ProtectedRoute requireModule="seguridad"><CompanyConfig /></ProtectedRoute>} />
        <Route path="tareas" element={<TareasYCalendario />} />
        <Route path="mi-cartera" element={<MiCartera />} />
        <Route path="comparador-costos" element={<ProtectedRoute requireModule="compras"><CostComparator /></ProtectedRoute>} />
        <Route path="gastos-operativos" element={<ProtectedRoute requireModule="contabilidad"><GastosOperativos /></ProtectedRoute>} />
        <Route path="liquidaciones-viaticos" element={<LiquidacionesViaticos />} />
        <Route path="devoluciones-proveedor" element={<ProtectedRoute requireModule="bodega"><DevolucionesProveedor /></ProtectedRoute>} />
        <Route path="calendario" element={<ProtectedRoute><TareasYCalendario /></ProtectedRoute>} />
      </Route>
    </Routes>
  );
}

function App() {
  return (
    <div className="App">
      <AuthProvider>
        <BrowserRouter>
          <AppRoutes />
          <Toaster position="top-right" richColors />
        </BrowserRouter>
      </AuthProvider>
    </div>
  );
}

export default App;
